import { useCallback, useEffect, useRef, useState } from 'react';
import { EvidenceScope, Overview, getOverview } from '../../../api/cafAutomation';
import { isMissingRoute } from '../saveEngine';
import { LoadStatus } from './useAiSwitch';

export interface OverviewState { status: LoadStatus; data: Overview | null; failed: boolean; reload: () => void }

export const useAutomationOverview = (days: number, scope: EvidenceScope = 'caf-workflows-1'): OverviewState => {
  const [status, setStatus] = useState<LoadStatus>('loading');
  const [data, setData] = useState<Overview | null>(null);
  const [failed, setFailed] = useState(false);
  const [tick, setTick] = useState(0);
  const hasData = useRef(false);
  const loadedScope = useRef<EvidenceScope | null>(null);
  const reload = useCallback(() => setTick((count) => count + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    if (loadedScope.current !== scope) {
      setStatus('loading');
      setFailed(false);
    }
    const run = async () => {
      try {
        const next = await getOverview(days, signal, scope);
        if (signal.aborted) return;
        if (!next || !next.switch || (scope === 'phase1' && !next.evidence) ||
          (next.evidence && next.evidence.scope !== scope))
          throw new Error('unexpected overview');
        hasData.current = true;
        loadedScope.current = scope;
        setData(next);
        setFailed(false);
        setStatus('ready');
      } catch (err) {
        if (signal.aborted) return;
        if (isMissingRoute(err)) {
          setStatus('missing');
        } else if (hasData.current && loadedScope.current === scope) {
          setFailed(true);
          setStatus('ready');
        } else {
          setFailed(false);
          setStatus('error');
        }
      }
    };
    void run();
    return () => controller.abort();
  }, [days, scope, tick]);

  return { status, data, failed, reload };
};
